import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/constants/icons';
import { useTrack } from '@/hooks/useTrack';

interface AITokensExhaustedModalProps {
  open: boolean;
  onClose: () => void;
}

export const AITokensExhaustedModal = ({ open, onClose }: AITokensExhaustedModalProps) => {
  const { t } = useTranslation('ai');
  const navigate = useNavigate();
  const track = useTrack();

  useEffect(() => {
    if (open) track('gate_shown', { key: 'ai_tokens', kind: 'quota', from: 'tokens' });
  }, [open, track]);

  const goToShop = () => {
    track('upsell_clicked', { key: 'ai_tokens', kind: 'quota', from: 'tokens' });
    onClose();
    navigate('/shop', { state: { entry: 'token_modal' } });
  };

  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} size="sm" showCloseButton raw>
      <div className="flex flex-col items-center text-center p-6">
        {/* Icon */}
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
          <Icon name={ICONS.MAGIC_WAND} size="lg" color="text-amber-600" ariaHidden />
        </div>

        {/* Title */}
        <h2 className="text-lg font-bold text-gray-900 mb-2">
          {t('ai_tokens_exhausted_title', { defaultValue: 'AI Tokens Used Up' })}
        </h2>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-6">
          {t('ai_tokens_exhausted_desc', {
            defaultValue:
              "You've used all your AI tokens for this month. Buy a token pack to keep going, or upgrade your plan.",
          })}
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-2 w-full">
          <Button
            variant="primary"
            size="sm"
            onClick={goToShop}
            className="w-full"
          >
            <Icon name={ICONS.MAGIC_WAND} size="xs" className="mr-1.5" ariaHidden />
            {t('ai_tokens_exhausted_buy_pack', { defaultValue: 'Buy a token pack' })}
          </Button>
          <div className="flex gap-3 w-full">
            <Button variant="secondary" size="sm" onClick={onClose} className="flex-1">
              {t('ai_tokens_exhausted_dismiss', { defaultValue: 'Maybe Later' })}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={goToShop}
              className="flex-1"
            >
              <Icon name={ICONS.CROWN} size="xs" className="mr-1.5" ariaHidden />
              {t('ai_tokens_exhausted_upgrade', { defaultValue: 'Upgrade Plan' })}
            </Button>
          </div>
        </div>

        {/* Hint */}
        <p className="text-xs text-gray-400 mt-4">
          {t('ai_tokens_exhausted_hint', { defaultValue: 'Your monthly allowance resets each month. Token packs never expire.' })}
        </p>
      </div>
    </Modal>
  );
};
